import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getBillboardControl,
  getKnownBillboardDevices,
  getDeviceTelemetry,
  sendSimulatedDeviceSpeed,
  type DeviceTelemetrySnapshot,
} from '../../api/billboard';

const DEFAULT_DEVICE_ID = 'billboard-1';
const CUSTOM_DEVICE_OPTION = '__custom__';

export const BillboardDeviceSimulator: React.FC = () => {
  const [deviceId, setDeviceId] = useState(DEFAULT_DEVICE_ID);
  const [knownDevices, setKnownDevices] = useState<DeviceTelemetrySnapshot[]>([]);
  const [loadingKnownDevices, setLoadingKnownDevices] = useState(false);
  const [speedMph, setSpeedMph] = useState(35);
  const [speedLimitMph, setSpeedLimitMph] = useState<number | null>(null);
  const [telemetry, setTelemetry] = useState<DeviceTelemetrySnapshot | null>(null);
  const [loadingTelemetry, setLoadingTelemetry] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trimmedDeviceId = deviceId.trim();
  const knownDeviceIds = knownDevices.map((device) => device.deviceId);
  const isCustomDevice = trimmedDeviceId.length > 0 && !knownDeviceIds.includes(trimmedDeviceId);

  useEffect(() => {
    let cancelled = false;

    const loadControl = async () => {
      try {
        const control = await getBillboardControl();
        if (!cancelled) {
          setSpeedLimitMph(control.speedLimitMph);
        }
      } catch {
        if (!cancelled) {
          setSpeedLimitMph(null);
        }
      }
    };

    void loadControl();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadKnownDevices = async () => {
      setLoadingKnownDevices(true);
      try {
        const devices = await getKnownBillboardDevices();
        if (cancelled) {
          return;
        }

        setKnownDevices(devices);
        setError(null);

        if (!trimmedDeviceId && devices[0]?.deviceId) {
          setDeviceId(devices[0].deviceId);
        }
      } catch {
        if (!cancelled) {
          setKnownDevices([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingKnownDevices(false);
        }
      }
    };

    void loadKnownDevices();

    return () => {
      cancelled = true;
    };
  }, [trimmedDeviceId]);

  useEffect(() => {
    let cancelled = false;

    const loadTelemetry = async () => {
      if (!trimmedDeviceId) {
        setTelemetry(null);
        return;
      }

      setLoadingTelemetry(true);
      try {
        const snapshot = await getDeviceTelemetry(trimmedDeviceId);
        if (!cancelled) {
          setTelemetry(snapshot);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setTelemetry(null);
          setError('Unable to load the current device snapshot.');
        }
      } finally {
        if (!cancelled) {
          setLoadingTelemetry(false);
        }
      }
    };

    void loadTelemetry();

    return () => {
      cancelled = true;
    };
  }, [trimmedDeviceId]);

  const handleSend = async () => {
    if (!trimmedDeviceId) {
      setError('Enter a device ID before sending telemetry.');
      return;
    }

    setSending(true);
    setMessage(null);
    setError(null);

    try {
      await sendSimulatedDeviceSpeed(trimmedDeviceId, speedMph);
      const snapshot = await getDeviceTelemetry(trimmedDeviceId);
      const devices = await getKnownBillboardDevices();

      setTelemetry(snapshot);
      setKnownDevices(devices);
      setMessage(`Sent ${speedMph} mph to ${trimmedDeviceId}.`);
    } catch {
      setError('Failed to send simulated speed telemetry.');
    } finally {
      setSending(false);
    }
  };

  const playerPath = `/billboard?deviceId=${encodeURIComponent(trimmedDeviceId || DEFAULT_DEVICE_ID)}`;
  const isOverLimit = typeof speedLimitMph === 'number' && speedMph > speedLimitMph;
  const selectedDeviceValue = isCustomDevice
    ? CUSTOM_DEVICE_OPTION
    : trimmedDeviceId || knownDevices[0]?.deviceId || CUSTOM_DEVICE_OPTION;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Billboard device simulator</h2>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
          Send test speed data to a specific billboard device without showing controls on the billboard screen.
        </p>
      </div>

      <div className="space-y-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
        <div>
          <div className="flex items-center justify-between gap-2">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Known billboard</label>
            {loadingKnownDevices ? <span className="text-[11px] text-slate-500 dark:text-slate-400">Loading saved devices…</span> : null}
          </div>
          <select
            value={selectedDeviceValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value === CUSTOM_DEVICE_OPTION) {
                if (!isCustomDevice) {
                  setDeviceId('');
                }
                return;
              }
              setDeviceId(value);
              setMessage(null);
              setError(null);
            }}
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {knownDevices.length === 0 ? (
              <option value={CUSTOM_DEVICE_OPTION}>No saved devices yet</option>
            ) : (
              knownDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.deviceId}
                </option>
              ))
            )}
            <option value={CUSTOM_DEVICE_OPTION}>Custom device ID</option>
          </select>
          {(knownDevices.length === 0 || selectedDeviceValue === CUSTOM_DEVICE_OPTION) && (
            <input
              type="text"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              className="mt-2 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={DEFAULT_DEVICE_ID}
            />
          )}
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Saved billboards come from device telemetry. Choose one here, or enter a new device ID to seed another billboard.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Simulated speed</label>
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{speedMph} mph</span>
          </div>
          <input
            type="range"
            min={0}
            max={80}
            step={1}
            value={speedMph}
            onChange={(e) => setSpeedMph(Number(e.target.value) || 0)}
            className="mt-2 w-full accent-emerald-500"
          />
          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
            <span>
              {typeof speedLimitMph === 'number' ? `Current limit: ${speedLimitMph} mph` : 'Current limit unavailable'}
            </span>
            <span className={isOverLimit ? 'font-medium text-red-600' : 'font-medium text-emerald-600'}>
              {isOverLimit ? 'Will blank' : 'Will play'}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
          >
            {sending ? 'Sending…' : 'Send speed to device'}
          </button>
          <button
            type="button"
            onClick={() => setSpeedMph(typeof speedLimitMph === 'number' ? Math.max(speedLimitMph - 10, 0) : 35)}
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50"
          >
            Safe preset
          </button>
          <button
            type="button"
            onClick={() => setSpeedMph(typeof speedLimitMph === 'number' ? Math.min(speedLimitMph + 10, 80) : 55)}
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50"
          >
            Blanking preset
          </button>
          <Link
            to={playerPath}
            className="inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            Open billboard player
          </Link>
        </div>

        {message && <p className="text-xs text-emerald-700">{message}</p>}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-xs text-slate-700 dark:text-slate-300">
        <div className="flex items-center justify-between">
          <p className="font-medium text-slate-900 dark:text-slate-100">Latest device snapshot</p>
          {loadingTelemetry ? <span className="text-slate-500 dark:text-slate-400">Loading…</span> : null}
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          <div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Device</p>
            <p className="mt-0.5 break-all">{telemetry?.deviceId || trimmedDeviceId || '—'}</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Last speed</p>
            <p className="mt-0.5">
              {typeof telemetry?.lastSpeedMph === 'number' ? `${telemetry.lastSpeedMph} mph` : '—'}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Last event</p>
            <p className="mt-0.5">
              {telemetry?.lastEventAt ? new Date(telemetry.lastEventAt).toLocaleString() : '—'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillboardDeviceSimulator;